import { useEffect, useRef, useState } from 'react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { confirmDialog } from '../../lib/confirmDialog';

const ATTACHMENT_BUCKET = 'portal-assets';

const formatSize = (bytes = 0) => {
  if (!bytes) return '0 KB';
  const units = ['B', 'KB', 'MB', 'GB'];
  const idx = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, idx);
  return `${value.toFixed(idx === 0 ? 0 : 1)} ${units[idx]}`;
};

export default function AttachmentSectionBuilder({ teamId, sectionId, items = [], onItemsUpdate }) {
  const [localItems, setLocalItems] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const fileInputRef = useRef(null);
  const [newItem, setNewItem] = useState({
    label: '',
    file: null
  });

  useEffect(() => {
    const filtered = items.filter((item) => item.section_id === sectionId).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    setLocalItems(filtered);
  }, [items, sectionId]);

  const canMutate = Boolean(teamId && sectionId);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setNewItem((prev) => ({ ...prev, file, label: prev.label || file.name }));
  };

  const uploadFile = async (file) => {
    const fileExt = file.name.split('.').pop() || 'bin';
    const storagePath = `${teamId}/attachments/${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`;

    const { error } = await supabase.storage
      .from(ATTACHMENT_BUCKET)
      .upload(storagePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) throw error;

    const { data } = supabase.storage.from(ATTACHMENT_BUCKET).getPublicUrl(storagePath);
    if (!data?.publicUrl) {
      throw new Error('Unable to generate public URL for attachment.');
    }

    return { publicUrl: data.publicUrl, storagePath };
  };

  const handleAddItem = async () => {
    if (!canMutate) {
      toast.error('Please save the section before adding attachments.');
      return;
    }

    if (!newItem.file) {
      toast.error('Select a file to upload.');
      return;
    }

    setSaving(true);
    try {
      const { publicUrl, storagePath } = await uploadFile(newItem.file);

      const insertPayload = {
        team_id: teamId,
        section_id: sectionId,
        file_name: newItem.label.trim() || newItem.file.name,
        file_url: publicUrl,
        storage_path: storagePath,
        file_size: newItem.file.size,
        sort_order: localItems.length,
        visible: true
      };

      const { data, error } = await supabase
        .from('portal_attachment_items')
        .insert(insertPayload)
        .select()
        .single();

      if (error) throw error;

      const updated = [...localItems, data];
      setLocalItems(updated);
      onItemsUpdate?.(sectionId, updated);
      setNewItem({ label: '', file: null });
      if (fileInputRef.current) fileInputRef.current.value = '';
      setIsAdding(false);
      toast.success('Attachment uploaded');
    } catch (error) {
      console.error('Error uploading attachment:', error);
      const message = error?.message?.includes('Storage bucket not found')
        ? 'Portal assets bucket not found. Please create a "portal-assets" bucket in Supabase Storage and mark it public.'
        : error.message || 'Failed to upload attachment.';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateItem = async (itemId, updates) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('portal_attachment_items')
        .update(updates)
        .eq('id', itemId);

      if (error) throw error;

      const updated = localItems.map((item) => (item.id === itemId ? { ...item, ...updates } : item));
      setLocalItems(updated);
      onItemsUpdate?.(sectionId, updated);
      setEditingId(null);
      toast.success('Attachment updated');
    } catch (error) {
      console.error('Error updating attachment:', error);
      toast.error('Failed to update attachment: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (item) => {
    const confirmed = await confirmDialog('Delete this attachment?', {
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    if (!confirmed) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('portal_attachment_items')
        .delete()
        .eq('id', item.id);

      if (error) throw error;

      if (item.storage_path) {
        await supabase.storage.from(ATTACHMENT_BUCKET).remove([item.storage_path]);
      }

      const updated = localItems.filter((attachment) => attachment.id !== item.id);
      setLocalItems(updated);
      onItemsUpdate?.(sectionId, updated);
      toast.success('Attachment removed');
    } catch (error) {
      console.error('Error deleting attachment:', error);
      toast.error('Failed to delete attachment: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleVisibility = async (item) => {
    setSaving(true);
    try {
      const nextVisible = !item.visible;
      const { error } = await supabase
        .from('portal_attachment_items')
        .update({ visible: nextVisible })
        .eq('id', item.id);

      if (error) throw error;

      const updated = localItems.map((attachment) =>
        attachment.id === item.id ? { ...attachment, visible: nextVisible } : attachment
      );
      setLocalItems(updated);
      onItemsUpdate?.(sectionId, updated);
      toast.success(`Attachment ${nextVisible ? 'shown' : 'hidden'} on portal`);
    } catch (error) {
      console.error('Error toggling attachment visibility:', error);
      toast.error('Failed to update visibility: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReorder = async (activeId, overId) => {
    if (activeId === overId) return;

    const fromIndex = localItems.findIndex((item) => item.id === activeId);
    const toIndex = localItems.findIndex((item) => item.id === overId);
    if (fromIndex === -1 || toIndex === -1) return;

    const reordered = [...localItems];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);

    const updates = reordered.map((item, idx) => ({ ...item, sort_order: idx }));
    setLocalItems(updates);

    setSaving(true);
    try {
      for (const item of updates) {
        await supabase
          .from('portal_attachment_items')
          .update({ sort_order: item.sort_order })
          .eq('id', item.id);
      }
      onItemsUpdate?.(sectionId, updates);
    } catch (error) {
      console.error('Error reordering attachments:', error);
      toast.error('Failed to reorder attachments');
      setLocalItems(localItems);
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!active || !over) return;
    handleReorder(active.id, over.id);
  };

  return (
    <div className="space-y-3" aria-busy={saving}>
      <div className="flex items-center justify-between mb-3">
        <label className="block text-sm font-medium text-gray-700">Downloads & Attachments</label>
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            + Add Attachment
          </button>
        )}
      </div>

      {isAdding && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
          <input
            type="text"
            placeholder="Display name (defaults to file name)"
            value={newItem.label}
            onChange={(e) => setNewItem((prev) => ({ ...prev, label: e.target.value }))}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className="w-full text-sm text-gray-600"
          />
          <div className="flex gap-2">
            <button
              onClick={handleAddItem}
              disabled={saving || !newItem.file}
              className="flex-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Uploading...' : 'Upload'}
            </button>
            <button
              onClick={() => {
                setIsAdding(false);
                setNewItem({ label: '', file: null });
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
              disabled={saving}
              className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {localItems.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">
          No attachments yet. Upload handouts, schedules, or important documents for easy access.
        </p>
      ) : (
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={localItems.map((item) => item.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {localItems.map((item) => (
                <SortableAttachmentCard key={item.id} item={item}>
                  {({ attributes, listeners }) => (
                    editingId === item.id ? (
                      <AttachmentEditForm
                        item={item}
                        saving={saving}
                        onCancel={() => setEditingId(null)}
                        onSave={(updates) => handleUpdateItem(item.id, updates)}
                      />
                    ) : (
                      <div className="flex flex-col gap-2">
                        <div className="flex items-start gap-3">
                          <button
                            {...attributes}
                            {...listeners}
                            className="px-2 py-2 text-xs text-gray-500 hover:bg-gray-100 rounded"
                            title="Drag to reorder"
                            aria-label="Drag attachment to reorder"
                            type="button"
                          >
                            ☰
                          </button>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 text-sm">{item.file_name}</h4>
                            <p className="text-xs text-gray-500">{formatSize(item.file_size)}</p>
                            <p className="text-[11px] text-gray-400 mt-1 break-all">{item.file_url}</p>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => setEditingId(item.id)}
                            disabled={saving}
                            className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
                          >
                            Rename
                          </button>
                          <button
                            onClick={() => handleToggleVisibility(item)}
                            disabled={saving}
                            className={`text-xs px-2 py-1 rounded ${
                              item.visible
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            } disabled:opacity-50`}
                          >
                            {item.visible ? '✓ Visible' : '✕ Hidden'}
                          </button>
                          <button
                            onClick={() => handleDeleteItem(item)}
                            disabled={saving}
                            className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    )
                  )}
                </SortableAttachmentCard>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <p className="text-xs text-gray-500">
        Attachments are stored securely and shared via public links so families can download them anytime.
      </p>
    </div>
  );
}

function SortableAttachmentCard({ item, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    cursor: isDragging ? 'grabbing' : 'grab'
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border rounded-lg p-3 ${item.visible ? 'bg-white border-gray-200' : 'bg-gray-100 border-gray-300'} ${
        isDragging ? 'shadow-lg z-10' : ''
      }`}
    >
      {children({ attributes, listeners })}
    </div>
  );
}

function AttachmentEditForm({ item, onSave, onCancel, saving }) {
  const [label, setLabel] = useState(item.file_name || '');

  const handleSubmit = () => {
    if (!label.trim()) {
      toast.error('Attachment name cannot be empty');
      return;
    }
    onSave({ file_name: label.trim() });
  };

  return (
    <div className="space-y-2">
      <input
        type="text"
        placeholder="Attachment name"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <div className="flex gap-2">
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="flex-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={onCancel}
          disabled={saving}
          className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

